import { useEffect, useRef } from 'react';
import { User, UserRole } from '../../../shared/src/types/user';

interface RoleChangeEvent {
  timestamp: string;
  previousRole: UserRole | undefined;
  newRole: UserRole | undefined;
  userId: string;
  source: string;
  userEmail: string;
}

interface UseRoleChangeDetectionOptions {
  onRoleChange?: (event: RoleChangeEvent) => void;
  logToConsole?: boolean;
  saveToStorage?: boolean;
}

/**
 * Hook para detectar e auditar mudanças de papel do usuário
 * IMPLEMENTADO PARA RESOLVER PROBLEMA: PRODUCAO -> REVENDA temporário
 */
export function useRoleChangeDetection(
  user: User | null,
  options: UseRoleChangeDetectionOptions = {}
) {
  const {
    onRoleChange,
    logToConsole = true,
    saveToStorage = true
  } = options;

  const previousRoleRef = useRef<UserRole | undefined>(undefined);
  const userIdRef = useRef<string>('');
  const changeCountRef = useRef<number>(0);

  useEffect(() => {
    if (!user) return;

    const currentRole = user.papel;
    const previousRole = previousRoleRef.current;
    const isFirstLoad = previousRoleRef.current === undefined;

    // Armazenar referências atuais
    previousRoleRef.current = currentRole;
    userIdRef.current = user.id;

    // Não reportar mudança na primeira carga
    if (isFirstLoad) {
      if (logToConsole) {
        console.log(`🔍 [ROLE-AUDIT] Inicial - Usuário: ${user.email}, Papel: ${currentRole}`, {
          userId: user.id,
          timestamp: new Date().toISOString()
        });
      }
      return;
    }

    // Detectar mudança de papel
    if (previousRole !== currentRole) {
      changeCountRef.current += 1;
      
      const changeEvent: RoleChangeEvent = {
        timestamp: new Date().toISOString(),
        previousRole,
        newRole: currentRole,
        userId: user.id,
        source: 'useRoleChangeDetection',
        userEmail: user.email
      };

      // Log crítico para o problema PRODUCAO -> REVENDA
      if (previousRole === 'PRODUCAO' && currentRole === 'REVENDA') {
        console.error(`🚨 [ROLE-AUDIT] PROBLEMA DETECTADO! PRODUCAO -> REVENDA`, {
          ...changeEvent,
          changeCount: changeCountRef.current,
          stackTrace: new Error().stack
        });
      } else if (previousRole === 'REVENDA' && currentRole === 'PRODUCAO') {
        console.warn(`🔄 [ROLE-AUDIT] Reversão detectada: REVENDA -> PRODUCAO`, {
          ...changeEvent,
          changeCount: changeCountRef.current
        });
      } else if (logToConsole) {
        console.log(`🔄 [ROLE-AUDIT] Mudança de papel detectada:`, {
          ...changeEvent,
          changeCount: changeCountRef.current
        });
      }

      // Salvar no localStorage para auditoria
      if (saveToStorage) {
        const storageKey = 'role-change-audit';
        const existingLogs = JSON.parse(localStorage.getItem(storageKey) || '[]');
        existingLogs.push(changeEvent);
        
        // Manter apenas os últimos 50 eventos para não sobrecarregar
        if (existingLogs.length > 50) {
          existingLogs.splice(0, existingLogs.length - 50);
        }
        
        localStorage.setItem(storageKey, JSON.stringify(existingLogs));
      }

      // Callback customizado
      if (onRoleChange) {
        onRoleChange(changeEvent);
      }
    }
  }, [user?.papel, user?.id, user?.email, logToConsole, saveToStorage, onRoleChange]);

  return {
    changeCount: changeCountRef.current,
    currentRole: user?.papel,
    userId: user?.id
  };
}

/**
 * Função utilitária para recuperar logs de auditoria do localStorage
 */
export function getRoleChangeAuditLogs(): RoleChangeEvent[] {
  try {
    return JSON.parse(localStorage.getItem('role-change-audit') || '[]');
  } catch (error) {
    console.error('Erro ao recuperar logs de auditoria:', error);
    return [];
  }
}

/**
 * Função utilitária para limpar logs de auditoria
 */
export function clearRoleChangeAuditLogs(): void {
  localStorage.removeItem('role-change-audit');
}

/**
 * Função para forçar validação de papel com o servidor
 */
export async function validateRoleWithServer(userId: string): Promise<UserRole | null> {
  try {
    console.log(`🔍 [ROLE-AUDIT] Validando papel no servidor para usuário: ${userId}`);
    
    const response = await fetch(`/api/users/validate-role/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      console.log(`✅ [ROLE-AUDIT] Papel validado no servidor:`, data.role);
      return data.role;
    } else {
      console.error(`❌ [ROLE-AUDIT] Erro na validação do servidor:`, response.status);
      return null;
    }
  } catch (error) {
    console.error(`❌ [ROLE-AUDIT] Erro ao validar papel:`, error);
    return null;
  }
}